import React from "react";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import toast from "react-hot-toast";

import { AuthContext } from "../../context";
import ClientChip from "./client-chip";
import UserChip from "./user-chip";
import TeamChip from "./team-chip";
import RoleChip, { type RoleChipValue } from "./role-chip";
import {
  useAddUser,
  useRemoveUserFromClient,
  useGetClient,
  useGetCurrentUser,
  useListUsers,
  useUpdateOrganisationMembership,
  useUpdateUserType,
} from "../../store";
import { User, UserType } from "../../types";
import { TeamSummary } from "../../types.new";
import { canManageTeamRole } from "../../utils/user-permissions";

type RoleOption = {
  role: RoleChipValue;
  userType: UserType;
};

interface EditableRoleChipProps {
  user: User;
  team?: TeamSummary;
  chipSize?: "small" | "medium";
  chipHeight?: number | string;
  chipFontSize?: number | string;
}

const getRoleChipValue = (user: User, teamId?: string): RoleChipValue => {
  // TODO eventually change roles so dev admin wont show as dev admin in roles, ie devs can be other roles in a client
  if (user.userType === UserType.DeveloperAdmin) return "Developer Admin";

  if (user.userType === UserType.SysAdmin) return "Client Admin";

  if (teamId) {
    return user.adminIds.includes(teamId) ? "Team Admin" : "Member";
  }

  if (user.userType === UserType.Guest) return "Guest";
  if (user.userType === UserType.Blocked) return "Blocked";
  if (user.userType === UserType.Inactive) return "Past Member";

  return "Member";
};

const getRoleSwapOptions = (
  currentRole: RoleChipValue,
  viewContext: "client" | "team",
): RoleOption[] => {
  switch (currentRole) {
    case "Client Admin":
      return [{ role: "Member", userType: UserType.User }];
    case "Member":
      if (viewContext === "client") {
        return [
          { role: "Past Member", userType: UserType.Inactive },
          { role: "Client Admin", userType: UserType.SysAdmin },
        ];
      }
      return [{ role: "Team Admin", userType: UserType.OrgAdmin }];
    case "Past Member":
      return [{ role: "Member", userType: UserType.User }];
    case "Team Admin":
      return [{ role: "Member", userType: UserType.User }];
    default:
      return [];
  }
};

const getRoleOptionDescription = (
  currentRole: RoleChipValue,
  targetRole: RoleChipValue,
): string => {
  if (targetRole === "Client Admin") {
    return "Grants client admin access and management permissions.";
  }

  if (targetRole === "Team Admin") {
    return "Allows this user to manage team members.";
  }

  if (targetRole === "Member" && currentRole === "Client Admin") {
    return "Removes admin rights and keeps standard member access.";
  }

  if (targetRole === "Member" && currentRole === "Team Admin") {
    return "Removes team admin rights and keeps standard access.";
  }

  if (targetRole === "Member" && currentRole === "Past Member") {
    return "Reactivates this user as an active member. Will send them an email notification.";
  }

  if (targetRole === "Past Member") {
    return "Marks this user as inactive while preserving their user record. They will be removed from teams and won't be visible by name in the attendee selection forms. They can be reactivated later.";
  }

  if (targetRole === "Blocked") {
    return "Blocks this user from accessing the platform.";
  }

  return "Updates this user's role.";
};

const getCurrentRoleDescription = (role: RoleChipValue): string => {
  switch (role) {
    case "Client Admin":
      return "Can manage users, teams and key settings at the client level.";
    case "Team Admin":
      return "Can manage members and admin access for their team.";
    case "Member":
      return "Has standard access.";
    case "Past Member":
      return "Inactive user that can be reactivated later.";
    case "Blocked":
      return "Access is blocked and the account cannot be used.";
    case "Guest":
      return "Limited guest access without a full member account.";
    case "Developer Admin":
      return "Highest internal admin access for developer operations.";
    case "User":
    default:
      return "Current role applied to this user account.";
  }
};

const EditableRoleChip: React.FC<EditableRoleChipProps> = ({
  user,
  team,
  chipSize = "small",
  chipHeight = "20px",
  chipFontSize = "0.65rem",
}) => {
  const [open, setOpen] = React.useState(false);
  const [selectedTargetType, setSelectedTargetType] =
    React.useState<UserType | null>(null);
  const [selectedRemove, setSelectedRemove] = React.useState(false);
  const { clientId } = React.useContext(AuthContext);
  const { data: currentUser } = useGetCurrentUser();
  const { data: client } = useGetClient(clientId);
  const { data: clientUsers = [] } = useListUsers(true, clientId);

  const clientAdminCount = React.useMemo(
    () =>
      clientUsers.filter(
        (clientUser) => clientUser.userType === UserType.SysAdmin,
      ).length,
    [clientUsers],
  );

  const viewContext = Boolean(team) ? "team" : "client";

  const currentRole = React.useMemo(
    () => getRoleChipValue(user, team?.teamId),
    [team, user],
  );
  const roleOptions = React.useMemo(
    () => getRoleSwapOptions(currentRole, viewContext),
    [currentRole, viewContext],
  );

  const canManageClientRoles = currentUser
    ? currentUser.userType === UserType.SysAdmin ||
      currentUser.userType === UserType.DeveloperAdmin
    : false;
  const canManageCurrentTeamRole =
    team?.teamId !== undefined && canManageTeamRole(currentUser, team.teamId);

  const canEditRole =
    currentRole !== "Developer Admin" &&
    currentRole !== "Client Admin" &&
    currentRole !== "Blocked" &&
    roleOptions.length > 0 &&
    (team ? canManageCurrentTeamRole : canManageClientRoles);

  const onRoleUpdateSuccess = () => {
    toast.success("User role updated");
    setOpen(false);
  };

  const onRoleUpdateError = (error: unknown) => {
    console.error(error);
    toast.error("Failed to update user role");
  };

  const { mutate: updateUserRole, isPending: isRoleUpdatePending } =
    useUpdateUserType(onRoleUpdateSuccess, onRoleUpdateError);

  const onDeleteUserSuccess = () => {
    toast.success("User moved to past member");
    setOpen(false);
  };

  const onDeleteUserError = (error: unknown) => {
    console.error(error);
    toast.error("Failed to update user role");
  };

  const { mutate: removeUserFromClient, isPending: isDeleteUserPending } =
    useRemoveUserFromClient(onDeleteUserSuccess, onDeleteUserError);

  const onAddUserSuccess = () => {
    toast.success("User reactivated successfully");
    setOpen(false);
  };

  const onAddUserError = (error: unknown) => {
    console.error(error);
    toast.error("Failed to reactivate user");
  };

  const { mutate: addUser, isPending: isAddUserPending } = useAddUser(
    onAddUserSuccess,
    onAddUserError,
  );

  const onTeamRoleUpdateSuccess = () => {
    toast.success("Updated Team");
    setOpen(false);
  };

  const onTeamRoleUpdateError = (error: unknown) => {
    console.error(error);
    toast.error("Failed to update team role");
  };

  const { mutate: updateTeamMembership, isPending: isTeamRoleUpdatePending } =
    useUpdateOrganisationMembership(
      onTeamRoleUpdateSuccess,
      onTeamRoleUpdateError,
    );

  const isUpdating =
    isRoleUpdatePending ||
    isTeamRoleUpdatePending ||
    isDeleteUserPending ||
    isAddUserPending;
  const userDisplayName = user.fullName || user.email || "Unknown User";

  const closeDialog = () => {
    setOpen(false);
    setSelectedTargetType(null);
    setSelectedRemove(false);
  };

  const onChipClick = () => {
    if (!canEditRole || isUpdating) {
      return;
    }

    setSelectedTargetType(null);
    setSelectedRemove(false);
    setOpen(true);
  };

  const onConfirmRoleChange = () => {
    if (selectedTargetType === null) {
      return;
    }

    const targetType = selectedTargetType;

    if (team && team.teamId) {
      updateTeamMembership({
        input: {
          membership: targetType === UserType.OrgAdmin ? "admin" : "true",
          targetId: user.userId,
        },
        organisationId: team.teamId,
      });
      return;
    }

    if (targetType === user.userType) {
      setOpen(false);
      return;
    }

    const isDemotingClientAdmin =
      user.userType === UserType.SysAdmin && targetType !== UserType.SysAdmin;
    const hasKnownClientUsers = clientUsers.length > 0;

    if (
      !(team && team.teamId) &&
      isDemotingClientAdmin &&
      hasKnownClientUsers &&
      clientAdminCount <= 1
    ) {
      toast.error("At least one client admin must remain for this client.");
      return;
    }

    if (targetType === UserType.Inactive) {
      removeUserFromClient({ userId: user.userId, clientId: user.clientId });
      return;
    }

    if (user.userType === UserType.Inactive && targetType === UserType.User) {
      addUser({
        ...user,
        userType: UserType.User,
      });
      return;
    }

    updateUserRole({
      targetId: user.userId,
      userType: targetType,
    });
  };

  const onConfirmRemoveFromTeam = () => {
    if (!team?.teamId) {
      return;
    }

    updateTeamMembership({
      input: {
        membership: "false",
        targetId: user.userId,
      },
      organisationId: team.teamId,
    });
  };

  return (
    <>
      <RoleChip
        role={currentRole}
        size={chipSize}
        height={chipHeight}
        fontSize={chipFontSize}
        onClick={canEditRole ? onChipClick : undefined}
        sx={{
          cursor: canEditRole ? "pointer" : "default",
          opacity: isUpdating ? 0.65 : 1,
        }}
      />

      <Dialog
        open={open}
        onClose={() => {
          if (!isUpdating) {
            closeDialog();
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle component="div">
          <Typography variant="h5">Role Selection</Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.25}>
            <Stack spacing={0.8} alignItems="center">
              <UserChip name={userDisplayName} />
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
              sx={{ fontWeight: 600 }}
            >
              is currently a
            </Typography>

            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
              <Stack spacing={0.8} alignItems="center">
                <RoleChip
                  role={currentRole}
                  size="small"
                  height="20px"
                  fontSize="0.68rem"
                />
                <Typography variant="caption" color="text.secondary">
                  {getCurrentRoleDescription(currentRole)}
                </Typography>
              </Stack>
            </Paper>
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
              sx={{ fontWeight: 600 }}
            >
              of {viewContext}
            </Typography>
            <Stack alignItems="center">
              {team ? (
                <TeamChip
                  teamId={team.teamId || "unknown-team"}
                  name={team.name || "Unknown Team"}
                  color={team.color || "#055480"}
                />
              ) : (
                <ClientChip
                  clientId={client?.clientId || clientId || "unknown-client"}
                  name={client?.clientName || "Unknown Client"}
                  sx={{
                    border: 1,
                    borderColor: "#055480",
                    borderRadius: 3,
                    pr: 1,
                  }}
                />
              )}
            </Stack>

            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
              sx={{ fontWeight: 600 }}
            >
              they can be changed to
            </Typography>

            {roleOptions.map((roleOption, index) => (
              <Stack key={roleOption.userType} spacing={1}>
                <Paper
                  variant="outlined"
                  onClick={() => {
                    if (isUpdating) {
                      return;
                    }

                    setSelectedRemove(false);
                    setSelectedTargetType(roleOption.userType);
                  }}
                  sx={(theme) => ({
                    p: 1.25,
                    borderRadius: 1.5,
                    cursor: isUpdating ? "default" : "pointer",
                    borderColor:
                      selectedTargetType === roleOption.userType
                        ? theme.palette.primary.main
                        : undefined,
                    backgroundColor:
                      selectedTargetType === roleOption.userType
                        ? theme.palette.action.selected
                        : theme.palette.background.paper,
                    transition:
                      "background-color 120ms ease, border-color 120ms ease",
                    "&:hover": {
                      backgroundColor: isUpdating
                        ? theme.palette.background.paper
                        : theme.palette.action.hover,
                    },
                  })}
                >
                  <Stack spacing={0.75} alignItems="center">
                    <RoleChip
                      role={roleOption.role}
                      size="small"
                      height="20px"
                      fontSize="0.68rem"
                      sx={{ cursor: "inherit" }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {getRoleOptionDescription(currentRole, roleOption.role)}
                    </Typography>
                  </Stack>
                </Paper>
                {index < roleOptions.length - 1 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    textAlign="center"
                    sx={{ fontWeight: 600 }}
                  >
                    or
                  </Typography>
                )}
              </Stack>
            ))}
            {viewContext === "team" && (
              <Stack spacing={1}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  textAlign="center"
                  sx={{ fontWeight: 600 }}
                >
                  or
                </Typography>
                <Paper
                  variant="outlined"
                  onClick={() => {
                    if (isUpdating) {
                      return;
                    }

                    setSelectedTargetType(null);
                    setSelectedRemove(true);
                  }}
                  sx={(theme) => ({
                    p: 1.25,
                    borderRadius: 1.5,
                    cursor: isUpdating ? "default" : "pointer",
                    backgroundColor: selectedRemove
                      ? "#fdecec"
                      : theme.palette.background.paper,
                    color: "#b42318",
                    borderColor: "#f7c0bc",
                    transition:
                      "background-color 120ms ease, border-color 120ms ease",
                    "&:hover": {
                      backgroundColor: isUpdating
                        ? theme.palette.background.paper
                        : theme.palette.action.hover,
                    },
                  })}
                >
                  <Stack
                    direction={"row"}
                    spacing={0.75}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <PersonRemoveIcon
                      sx={{ color: "inherit", fontSize: "small" }}
                    />
                    <Typography variant="caption">
                      Remove them from the team.
                    </Typography>
                  </Stack>
                </Paper>
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeDialog}
            disabled={isUpdating}
            fullWidth
            variant="outlined"
          >
            Cancel
          </Button>
          {selectedRemove ? (
            <Button
              fullWidth
              variant="contained"
              color="error"
              startIcon={<PersonRemoveIcon />}
              onClick={onConfirmRemoveFromTeam}
              disabled={isUpdating}
              sx={{
                borderRadius: 999,
              }}
            >
              Remove
            </Button>
          ) : (
            <Button
              fullWidth
              variant="contained"
              startIcon={<CheckIcon />}
              onClick={onConfirmRoleChange}
              disabled={isUpdating || selectedTargetType === null}
              sx={{
                borderRadius: 999,
              }}
            >
              Confirm
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditableRoleChip;
